// Date & time display for devices. The Android time zone mapping isn't final,
// so the listing, the Device Modal and the bulk wizard all read it from here.

import * as Yup from "yup";

import { RdxGenVersion } from "../../../api/graphql/devices/devices";
import { DEVICE_LOCALES, DEVICE_TIME_ZONES } from "./deviceDateTimeOptions";

const byLabel = (a: { label: string }, b: { label: string }) =>
  a.label.localeCompare(b.label);

// The form holds a locale's tag; the device gets its generation's tag on save
export const DEVICE_LOCALE_OPTIONS = DEVICE_LOCALES.map(({ tag, name }) => ({
  value: tag,
  label: name,
})).sort(byLabel);

export const DEVICE_TIME_ZONE_OPTIONS = DEVICE_TIME_ZONES.map(
  ({ id, country, comment, utcOffsets }) => ({
    value: id,
    label: `${comment ? `${country} – ${comment}` : country} (UTC${utcOffsets})`,
  })
).sort(byLabel);

// A device reports its own generation's tag, e.g. "bs-BA" on Gen 1 for Bosnian
const localesByTag = new Map(
  DEVICE_LOCALES.flatMap((locale) =>
    [locale.tag, locale.gen1Tag, locale.gen2Tag].map((tag) => [tag, locale])
  )
);

const timeZonesById = new Map(
  DEVICE_TIME_ZONES.flatMap((timeZone) =>
    [timeZone.id, timeZone.ianaId].map((id) => [id, timeZone])
  )
);

// Values that aren't in the lists are kept as they are
export const toFormLocale = (locale?: string | null) =>
  locale ? (localesByTag.get(locale)?.tag ?? locale) : "";

export const toFormTimeZone = (timeZone?: string | null) =>
  timeZone ? (timeZonesById.get(timeZone)?.id ?? timeZone) : "";

// A device that hasn't reported its generation gets the plain tag
export const toDeviceLocale = (
  locale: string,
  genVersion?: RdxGenVersion | null
) => {
  const deviceLocale = localesByTag.get(locale);

  if (!deviceLocale) {
    return locale;
  }

  if (genVersion === "v1") {
    return deviceLocale.gen1Tag;
  }

  if (genVersion === "v2") {
    return deviceLocale.gen2Tag;
  }

  return deviceLocale.tag;
};

interface DateTimeCapableDevice {
  deviceSupportedFeature?: { dateAndTimeSettings?: boolean | null } | null;
}

// The backend owns the compatibility list; a device that hasn't reported is unsupported
export const supportsDateTimeSettings = (device: DateTimeCapableDevice) =>
  !!device.deviceSupportedFeature?.dateAndTimeSettings;

// Shared by the Device Modal and the bulk wizard
export const DateTimeSchema = Yup.object().shape({
  timeZone: Yup.string().required("Please select a time zone!"),
  locale: Yup.string().required("Please select a date format!"),
});

export interface DeviceTimeZone {
  name: string;
  offset?: string;
}

// The listing formats the same few zones on every render
const offsetFormatters = new Map<string, Intl.DateTimeFormat | null>();

const getOffsetFormatter = (timeZone: string) => {
  if (!offsetFormatters.has(timeZone)) {
    let formatter: Intl.DateTimeFormat | null = null;

    try {
      formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName: "longOffset",
      });
    } catch {
      // Not an IANA zone
    }

    offsetFormatters.set(timeZone, formatter);
  }

  return offsetFormatters.get(timeZone);
};

export const formatDeviceTimeZone = (
  timeZone?: string | null,
  date = new Date()
): DeviceTimeZone => {
  if (!timeZone) {
    return { name: "-" };
  }

  const formatter = getOffsetFormatter(timeZone);

  if (!formatter) {
    return { name: timeZone };
  }

  const offset = formatter
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;

  // Zones at UTC+0 come back as plain "GMT"
  return { name: timeZone, offset: offset === "GMT" ? "GMT+00:00" : offset };
};
